// Phase 6.0 — Tap-to-learn knowledge map for cannabinoid + terpene chips
// shown in the customer ProductDetailDrawer. Each entry is intentionally
// short (one warm sentence) so the inline expansion doesn't bloat the panel.
//
// Sources: cited from common cannabis education references (no medical claims).
// Keep entries concise and on-tone — avoid clinical jargon.

export const CANNABINOID_FACTS = {
  THCA:
    "Raw, non-intoxicating acid form of THC. Heat converts it to active THC when smoked or vaporized.",
  "THC-D9":
    "Delta-9 THC — the classic psychoactive cannabinoid responsible for the cannabis 'high'.",
  THC:
    "Active form of THCA after heat. The headline psychoactive in most flower.",
  CBGA:
    "The 'mother' cannabinoid; precursor to THC, CBD, and CBC. Non-intoxicating on its own.",
  CBG:
    "Calming, non-psychoactive cannabinoid often associated with focus and mood support.",
  CBD:
    "Non-intoxicating cannabinoid commonly associated with calm, balance, and recovery.",
  CBDA:
    "Acid form of CBD. Non-intoxicating; converts to CBD when heated.",
  CBC:
    "Minor non-intoxicating cannabinoid sometimes linked to mood and inflammation support.",
  CBN:
    "Forms as THC ages. Often associated with deeper, sleepier body relaxation.",
  THCVA:
    "Acidic precursor to THCV. Non-intoxicating in its raw form.",
  THCV:
    "Lighter cannabinoid sometimes described as energizing and appetite-suppressing.",
};

export const TERPENE_FACTS = {
  "Beta Caryophyllene":
    "Peppery, woody, spicy. The same molecule that gives black pepper its bite.",
  Caryophyllene:
    "Peppery, woody, spicy. The same molecule that gives black pepper its bite.",
  "Beta Myrcene":
    "Earthy, musky, mango-like. The most common terpene in cannabis flower.",
  Myrcene:
    "Earthy, musky, mango-like. The most common terpene in cannabis flower.",
  Limonene:
    "Bright citrus zest. Often described as uplifting and mood-brightening.",
  Linalool:
    "Soft floral lavender. Calming; also found in chamomile and mint.",
  Humulene:
    "Hoppy, earthy, woodsy. The terpene that gives hops their bitter character.",
  "Beta Pinene":
    "Fresh pine forest. Bright, clean, often associated with focus.",
  "Alpha Pinene":
    "Sharper pine note than beta. Crisp, herbaceous, evergreen.",
  Pinene:
    "Pine forest. Bright, fresh, often associated with focus.",
  Bisabolol:
    "Soft floral with chamomile-like sweetness. Gentle, slightly powdery.",
  Terpinolene:
    "Fresh, herbal, lightly citrus. Common in sativa-leaning chemovars.",
  Ocimene:
    "Sweet, herbal, woody with hints of fruit and mint.",
  "Caryophyllene Oxide":
    "Oxidized form of caryophyllene — sharper, peppery, slightly minty.",
  Nerolidol:
    "Soft woody-floral with apple and rose undertones.",
  Guaiol:
    "Cool pine-rose with a hint of fresh forest air.",
  "Trans-Nerolidol":
    "Subtle apple-rose woodiness — often a supporting note.",
};

export function cannabinoidFact(key) {
  if (!key) return null;
  const exact = CANNABINOID_FACTS[key];
  if (exact) return exact;
  // Loose match: ignore case + punctuation when comparing.
  const norm = String(key).toUpperCase().replace(/[^A-Z0-9]/g, '');
  for (const [k, v] of Object.entries(CANNABINOID_FACTS)) {
    if (k.toUpperCase().replace(/[^A-Z0-9]/g, '') === norm) return v;
  }
  return null;
}

export function terpeneFact(key) {
  if (!key) return null;
  if (TERPENE_FACTS[key]) return TERPENE_FACTS[key];
  // Loose match on alpha-only ignoring case/punct/whitespace.
  const norm = String(key).toLowerCase().replace(/[^a-z]/g, '');
  for (const [k, v] of Object.entries(TERPENE_FACTS)) {
    if (k.toLowerCase().replace(/[^a-z]/g, '') === norm) return v;
  }
  return null;
}
